import type { BrandConfig, BrandPalette } from "./index";
import {
  APPOINTEDD_DEFAULT_LIGHT_PALETTE,
  APPOINTEDD_DEFAULT_DARK_PALETTE,
} from "./index";

export type ResolvedTheme = "light" | "dark";

/**
 * Merges a stack of BrandConfig layers (e.g. [enterpriseConfig,
 * organisationConfig, micrositeConfig]) in ascending precedence order
 * (later layers override earlier ones, per-field within each palette), for
 * the given resolved theme, falling back to Appointedd static defaults for
 * any unset field.
 *
 * For each layer, a theme-specific `logoUrl`/`bannerUrl` (on `layer[theme]`)
 * takes precedence over that same layer's shared/flat `logoUrl`/`bannerUrl`
 * (on `layer.logoUrl`/`layer.bannerUrl`), which acts as a per-layer fallback
 * when the theme's palette doesn't specify its own.
 */
export function resolveBrandPalette(
  layers: Array<BrandConfig | undefined | null>,
  theme: ResolvedTheme,
): BrandPalette {
  const defaultPalette =
    theme === "dark"
      ? APPOINTEDD_DEFAULT_DARK_PALETTE
      : APPOINTEDD_DEFAULT_LIGHT_PALETTE;
  let merged: BrandPalette = { ...defaultPalette };
  for (const layer of layers) {
    const palette = layer?.[theme];
    // Per-theme logoUrl/bannerUrl override the shared/flat fields that live
    // directly on the BrandConfig layer (siblings to light/dark), which act
    // as a fallback when the theme-specific palette doesn't specify its own.
    const effective: BrandPalette = {
      ...palette,
      logoUrl: palette?.logoUrl ?? layer?.logoUrl,
      bannerUrl: palette?.bannerUrl ?? layer?.bannerUrl,
    };
    merged = {
      ...merged,
      ...Object.fromEntries(
        Object.entries(effective).filter(([, v]) => v !== undefined),
      ),
    };
  }
  return merged;
}

/**
 * Given a BrandConfig's brandThemeMode and an optional visitor override /
 * system preference, determines which theme should actually render.
 *
 * Precedence: explicit visitor override (if allowVisitorThemeOverride) >
 * brandThemeMode (if "light"/"dark") > systemPreference (if brandThemeMode
 * is "auto" or unset) > "light".
 */
export function resolveActiveTheme(params: {
  brandThemeMode?: BrandConfig["brandThemeMode"];
  allowVisitorThemeOverride?: boolean;
  visitorOverride?: ResolvedTheme | null;
  systemPreference?: ResolvedTheme | null;
}): ResolvedTheme {
  const {
    brandThemeMode,
    allowVisitorThemeOverride = true,
    visitorOverride,
    systemPreference,
  } = params;
  if (allowVisitorThemeOverride && visitorOverride) return visitorOverride;
  if (brandThemeMode === "light" || brandThemeMode === "dark")
    return brandThemeMode;
  if ((brandThemeMode === "auto" || !brandThemeMode) && systemPreference)
    return systemPreference;
  return "light";
}
